/**
 * @file PatientProfessionals.jsx — /mis-profesionales
 */
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Users, Search, CalendarDays } from "lucide-react";
import { usePatientProfessionals } from "../hooks/usePatients";
import { PATIENT_STRINGS } from "../constants/strings";
import { Avatar, Button, Card, Chip, Spinner } from "../ui";
import "./PatientProfessionals.css";

export default function PatientProfessionals() {
  const { professionals, isLoading } = usePatientProfessionals();

  if (isLoading) {
    return (
      <div className="pp-page">
        <div className="pp-loading">
          <Spinner size={28} />
        </div>
      </div>
    );
  }

  return (
    <div className="pp-page">
      <header className="pp-head">
        <span className="pp-eyebrow">Mis profesionales</span>
        <h1 className="pp-title">{PATIENT_STRINGS.MY_PROFESSIONALS_TITLE}</h1>
      </header>

      {professionals.length === 0 ? (
        <Card className="pp-empty">
          <Users size={36} className="pp-empty-icon" />
          <p className="pp-empty-text">{PATIENT_STRINGS.NO_PROFESSIONALS}</p>
          <p className="pp-empty-sub">{PATIENT_STRINGS.NO_PROFESSIONALS_CTA}</p>
          <Button asChild leftIcon={<Search size={14} />}>
            <RouterLink to="/profesionales">{PATIENT_STRINGS.BOOK_FIRST_CTA}</RouterLink>
          </Button>
        </Card>
      ) : (
        <div className="pp-grid">
          {professionals.map((entry) => {
            const pro = entry.professional;
            const user = pro?.user;
            const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
            const initials = `${(user?.firstName || "")[0] || ""}${(user?.lastName || "")[0] || ""}`;
            return (
              <Card key={pro.id} hover className="pp-card">
                <div className="pp-card-head">
                  <Avatar size="md" initials={initials} alt={name} />
                  <div className="pp-card-text">
                    <span className="pp-card-name">{name}</span>
                    {pro.specialty && (
                      <Chip tone="primary" size="sm">
                        {pro.specialty}
                      </Chip>
                    )}
                  </div>
                </div>
                <div className="pp-card-meta">
                  <span>
                    <strong>{entry.totalVisits}</strong> {PATIENT_STRINGS.TOTAL_VISITS}
                  </span>
                  <span className="pp-card-last">
                    {PATIENT_STRINGS.LAST_VISIT}:{" "}
                    {new Date(entry.lastVisitDate).toLocaleDateString("es-MX", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="pp-card-actions">
                  {pro.slug && (
                    <Button
                      asChild
                      leftIcon={<CalendarDays size={14} />}
                      size="sm"
                      style={{ flex: 1 }}
                    >
                      <RouterLink to={`/c/${pro.slug}`}>{PATIENT_STRINGS.BOOK_AGAIN}</RouterLink>
                    </Button>
                  )}
                  <Button asChild variant="outline" size="sm">
                    <RouterLink to={`/profesional/${pro.id}`}>
                      {PATIENT_STRINGS.VIEW_PROFILE}
                    </RouterLink>
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
